import {Injectable} from '@angular/core';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {isNext, salesChannel} from '../../utils/utils';
import {SessionService} from './session/session.service';
import {ApplicationContextService} from './configuration/application-context.service';
import {NotificationService} from './notification.service';
import {SessionStorage} from '../global/storage/session-storage.service';
import {SalesChannelAnalyticsService} from '../../../shared/services/sales-channel-analytics.service';
import {NotificationStatusConfig, SessionStatus, WebsocketNotification} from '../../models/notification';
import {SessionStatusCode} from '../../enums/session-status-code.enum';
import {NotificationStatusesConfig} from '../../configs/notification-statuses-config';
import {SessionDetailsResponse} from '../../models/session-details-response.model';
import {NotificationStateService} from '../../../shared/services/notification-state.service';
import {AppMode} from '../../../shared/models/app-mode.enum';

@Injectable({
  providedIn: 'root'
})
export class NextNotificationService {

  nextNotification: boolean;
  notificationStatusConfig: NotificationStatusConfig;
  status: SessionStatus;
  sessionValues: any;
  notification: WebsocketNotification;
  loading: boolean;
  private statusesConfig: NotificationStatusConfig[];
  private readonly GET_SESSION_TIMEOUT = 10000;
  private sessionUpdateActive: boolean = true;

  get message(): string {
    return this.status ? this.status.message : null;
  }

  get submitGoogleReviewButtonVisible(): boolean {
    if (!this.nextNotification) {
      return false;
    }
    if (isNotNullOrUndefined(this.notificationStatusConfig)) {
      return this.notificationStatusConfig.headerConfig.submitGoogleReviewButtonVisible && this.contextService.submitGoogleReviewVisible;
    }
    return false;
  }

  constructor(private sessionService: SessionService,
              private contextService: ApplicationContextService,
              private notificationService: NotificationService,
              private notificationStateService: NotificationStateService,
              private sessionStorage: SessionStorage,
              private salesChannelAnalyticsService: SalesChannelAnalyticsService) {
    this.initNextNotification();
  }

  init() {
    this.initStatusesConfig();
    this.loading = true;
    this.getSessionDetails();
    this.notificationService.notificationSubject.asObservable().subscribe(notification => {
      if (isNotNullOrUndefined(notification)) {
        this.handleNotification(notification);
      }
    });
  }

  initNextNotification(): void {
    this.nextNotification = isNext() && !this.contextService.isDiagnosticStationContext;
  }

  private getSessionDetails() {
    if (this.sessionUpdateActive) {
      this.sessionService.getSessionDetails()
          .then((resp: SessionDetailsResponse) => {
            if (resp && resp.values && resp.values.status) {
              this.sessionValues = resp.values;
              this.handleSessionStatus(resp.values);
            }
          })
          .catch(error => {
            if (error && error.status === 404) {
              this.handleNoPolicyInfo();
            }
          });
    }
  }

  private handleNotification(notification: WebsocketNotification): void {
    this.loading = false;
    this.notification = notification;

    switch (notification.body.status) {
      case 202:
        this.handleWaitingForPayment();
        break;
      case 200:
        this.handleProcessCompleted();
        break;
      case 402:
        this.handlePaymentCancelled();
        break;
      case 409:
        this.nextNotification = false;
        break;
      default:
    }

    this.notificationStateService.setAppMode(AppMode.READONLY);
    this.sendAnalyticsData();
    this.updateNotificationStatusConfig(NotificationStateSource.WEBOCKET);
  }

  private handleSessionStatus(sessionValues: any): void {
    this.status = sessionValues.status;
    if (isNotNullOrUndefined(this.status)) {
      switch (this.status.statusCode) {
          case SessionStatusCode.WAITING_FOR_PAYMENT:
            this.handleWaitingForPayment();
            break;
        case SessionStatusCode.PROCESS_COMPLETED:
          this.sendAnalyticsData();
          this.handleProcessCompleted();
          break;
          case SessionStatusCode.REGISTER_POLICY_ERROR:
            this.handleRegisterPolicyError();
            break;
        case SessionStatusCode.PAYMENT_CANCELED:
          this.handlePaymentCancelled();
          break;
        default:
          this.loading = false;
          if (this.status.code === 202) {
            this.handleWaitingForPayment();
          }
          if (this.status.code === 409) {
            this.nextNotification = false;
          }
      }

      this.updateNotificationStatusConfig(NotificationStateSource.SESSION);
    }
  }

  private handleWaitingForPayment() {
    this.loading = false;
    setTimeout(() => {
      this.getSessionDetails();
    }, this.GET_SESSION_TIMEOUT);
  }

  private handleProcessCompleted() {
    this.sessionUpdateActive = false;
    this.loading = false;
    this.clearStorage();
  }

  private async handlePaymentCancelled() {
    this.sessionUpdateActive = false;
    this.loading = false;
  }

  private async handleRegisterPolicyError() {
    this.sessionUpdateActive = false;
    this.loading = false;
  }

  private handleNoPolicyInfo(): void {

  }

  private updateNotificationStatusConfig(source: NotificationStateSource): void {
    let statusConfig = this.getStatusConfig(source);

    // display process completed notification for statuses without config
    if (!isNotNullOrUndefined(statusConfig)) {
      statusConfig = this.statusesConfig.find(config => config.codes.includes(SessionStatusCode.PROCESS_COMPLETED));
    }
    this.notificationStatusConfig = statusConfig;
  }

  private initStatusesConfig(): void {
    const statusesConfig = (new NotificationStatusesConfig(this.contextService)).createStatusesConfig();
    let config = statusesConfig.find(item => item.salesChannel === salesChannel());
    if (!isNotNullOrUndefined(config)) {
      config = statusesConfig.find(item => item.default);
    }
    this.statusesConfig = config.statuses;
  }

  private clearStorage(): void {
    this.sessionStorage.clear();
  }

  private sendAnalyticsData() {
    this.salesChannelAnalyticsService.sendDataAfterPaymentConfirmation();
  }

  private getStatusConfig(source: NotificationStateSource): NotificationStatusConfig {
    return this.statusesConfig.find(config => {
      switch (source) {
        case NotificationStateSource.SESSION:
          if (isNotNullOrUndefined(this.status)) {
            return config.codes.includes(this.status.statusCode);
          }
          break;
        case NotificationStateSource.WEBOCKET:
          if (isNotNullOrUndefined(this.notification) && isNotNullOrUndefined(this.notification.body)) {
            return config.status === this.notification.body.status;
          }
          break;
        default:
          return false;
      }
    });
  }
}

export enum NotificationStateSource {
  SESSION = 'SESSION',
  WEBOCKET = 'WEBOCKET',
}
